import {
  OrganizationListRSModel,
  ReportInvestmentFundsModel,
  ReportInvestmentFundsParamsModel,
  ReportLocalGovernmentFundsParamsModel,
  ReportLocalGovernmentFundsTableAModel,
  ReportLocalGovernmentFundsTableBModel,
  ReportOrganizingTableFilterSchema,
  ReportOrganizingTableFormat1Model,
  ReportOrganizingTableFormat2Model,
  ReportOrganizingTableFormat3Model,
  ReportOrganizingTableFormat4Model,
  ReportOrganizingTableFormat5Model,
  ReportStandardFundsForPrivateModel,
  ReportStandardFundsForPublicModel,
  ReportStandardFundsParamsModel,
  UsersListModel,
  reportFileType,
  reportFormatType,
} from '@/features/mold/models';
import {
  ReportFundSheetListModel,
  ReportReviewSheetListModel,
  ReportSegmentSheetListModel,
} from '@/features/mold/models/project';
import {
  downloadUsersUploadListCsv,
  getOrganizationListCsvRSData,
  getRsProjectListCsvData,
  getUserListCsvData,
} from '@/features/mold/utils';
import {
  convertInvestmentFundReportToCsv,
  getInvestmentReportCsvHeader,
} from '@/features/mold/utils/investment-fund/get-csv';
import {
  convertLocalGovernmentFundTableAToCsv,
  getLocalGovernmentFundTableACsvHeader,
} from '@/features/mold/utils/local-government-fund/tableA';
import {
  convertLocalGovernmentFundTableBToCsv,
  getLocalGovernmentFundTableBCsvHeader,
} from '@/features/mold/utils/local-government-fund/tableB';
import { getFormat1CsvData } from '@/features/mold/utils/organizing-table/format1/get-csv';
import { getFormat2CsvData } from '@/features/mold/utils/organizing-table/format2/get-csv';
import { getFormat3CsvData } from '@/features/mold/utils/organizing-table/format3/get-csv';
import { getFormat4CsvData } from '@/features/mold/utils/organizing-table/format4/get-csv';
import { getFormat5CsvData } from '@/features/mold/utils/organizing-table/format5/get-csv';
import {
  convertStandardFundReportForPrivateToCsv,
  getReportTargetFiscalYears,
  getStandardFundReportForPrivateAnnualDataCsvHeader,
  getStandardFundReportForPrivateCsvHeader,
} from '@/features/mold/utils/standard-fund/private-csv';
import {
  convertStandardFundReportForPublicToCsv,
  getStandardFundReportForPublicCsvHeader,
} from '@/features/mold/utils/standard-fund/public-csv';
import {
  type AmountUnit,
  EMPTY_DATA_ERROR,
  downloadCsv,
  type jsonType,
} from '@/utils';
import Papa from 'papaparse';
import type { z } from 'zod';
import {
  PaymentGroupUploadErrorModel,
  rsBatchCsvDownloadTargetType,
} from '../_models/batch';
import { getExportRsFileName } from './get-export-rs-file-name';

type Args = {
  jsonData: any;
  target: string;
  batchParams: z.infer<typeof jsonType>;
  unit: AmountUnit;
  dateCreated: string;
  isDebugMode: boolean;
};

export const downloadBatchCsv = ({
  target,
  batchParams,
  jsonData,
  dateCreated,
  isDebugMode,
}: Args) => {
  const csvTarget = rsBatchCsvDownloadTargetType.parse(target);
  const fileName = getExportRsFileName({
    jsonData,
    target: csvTarget,
    batchParams,
    fileType: reportFileType.Values.csv,
    dateCreated,
  });
  switch (csvTarget) {
    case 'rs-project-list': {
      const parsed = ReportReviewSheetListModel.parse(jsonData);
      if (!parsed.length) {
        throw new Error(EMPTY_DATA_ERROR);
      }
      const csvData = getRsProjectListCsvData({
        data: parsed,
        sheetType: 'RS',
        isDebugMode,
      });
      downloadCsv(csvData, fileName);
      break;
    }
    case 'ss-project-list': {
      const parsed = ReportSegmentSheetListModel.parse(jsonData);
      if (!parsed.length) {
        throw new Error(EMPTY_DATA_ERROR);
      }
      const csvData = getRsProjectListCsvData({
        data: parsed,
        sheetType: 'SS',
        isDebugMode,
      });
      downloadCsv(csvData, fileName);
      break;
    }
    case 'ks-project-list': {
      const parsed = ReportFundSheetListModel.parse(jsonData);
      if (!parsed.length) {
        throw new Error(EMPTY_DATA_ERROR);
      }
      const csvData = getRsProjectListCsvData({
        data: parsed,
        sheetType: 'KS',
        isDebugMode,
      });
      downloadCsv(csvData, fileName);
      break;
    }
    case 'ks-investment-list': {
      const parsed = ReportInvestmentFundsModel.parse(jsonData);
      if (!parsed.data.length) {
        throw new Error(EMPTY_DATA_ERROR);
      }

      const { fiscalYear, projectDetailUrlType } =
        ReportInvestmentFundsParamsModel.parse(batchParams);
      const last3FiscalYears = [fiscalYear - 2, fiscalYear - 1, fiscalYear];
      const headers = getInvestmentReportCsvHeader(last3FiscalYears);
      const csvData = convertInvestmentFundReportToCsv({
        headers,
        reports: parsed,
        isDebugMode,
        projectDetailUrlType,
      });

      downloadCsv(csvData, fileName);
      break;
    }
    case 'ks-standard-list': {
      const { fiscalYear, reportType, projectDetailUrlType } =
        ReportStandardFundsParamsModel.parse(batchParams);
      if (reportType === 'public') {
        const parsed = ReportStandardFundsForPublicModel.parse(jsonData);
        if (!parsed.data.length) {
          throw new Error(EMPTY_DATA_ERROR);
        }
        const csvData = convertStandardFundReportForPublicToCsv({
          headers: getStandardFundReportForPublicCsvHeader(fiscalYear),
          reports: parsed,
          isDebugMode,
          projectDetailUrlType,
        });
        downloadCsv(csvData, fileName);
      } else {
        const parsed = ReportStandardFundsForPrivateModel.parse(jsonData);
        if (!parsed.data.length) {
          throw new Error(EMPTY_DATA_ERROR);
        }

        const reportTargetFiscalYears = getReportTargetFiscalYears(fiscalYear);
        const headers = getStandardFundReportForPrivateCsvHeader(
          reportTargetFiscalYears,
        );

        const annualDataHeaders = reportTargetFiscalYears.reduce(
          (acc, year, i) => {
            const isLastOrSecondLastYear =
              i === reportTargetFiscalYears.length - 1 ||
              i === reportTargetFiscalYears.length - 2;

            acc[year] = getStandardFundReportForPrivateAnnualDataCsvHeader(
              year,
              isLastOrSecondLastYear,
            );

            return acc;
          },
          {},
        );
        const csvData = convertStandardFundReportForPrivateToCsv({
          headers,
          annualDataHeaders,
          reportTargetFiscalYears,
          reports: parsed,
          isDebugMode,
          projectDetailUrlType,
        });
        downloadCsv(csvData, fileName);
      }
      break;
    }
    case 'ks-local-government-list-A': {
      const parsed = ReportLocalGovernmentFundsTableAModel.parse(jsonData);
      if (!parsed.data.length) {
        throw new Error(EMPTY_DATA_ERROR);
      }

      const { fiscalYear, projectDetailUrlType } =
        ReportLocalGovernmentFundsParamsModel.parse(batchParams);
      const csvData = convertLocalGovernmentFundTableAToCsv({
        headers: getLocalGovernmentFundTableACsvHeader(fiscalYear),
        reports: parsed,
        projectDetailUrlType,
      });
      downloadCsv(csvData, fileName);
      break;
    }
    case 'ks-local-government-list-B': {
      const parsed = ReportLocalGovernmentFundsTableBModel.parse(jsonData);
      if (!parsed.data.length) {
        throw new Error(EMPTY_DATA_ERROR);
      }

      const { fiscalYear, projectDetailUrlType } =
        ReportLocalGovernmentFundsParamsModel.parse(batchParams);
      const csvData = convertLocalGovernmentFundTableBToCsv({
        headers: getLocalGovernmentFundTableBCsvHeader(fiscalYear),
        reports: parsed,
        isDebugMode,
        projectDetailUrlType,
      });
      downloadCsv(csvData, fileName);
      break;
    }
    case 'rs-report1': {
      const parsed = ReportOrganizingTableFormat1Model.parse(jsonData);
      if (parsed.policies.length === 0) {
        throw new Error(EMPTY_DATA_ERROR);
      }

      const params = ReportOrganizingTableFilterSchema.parse({
        ...(batchParams as object),
        formatNumber: reportFormatType.Values.format1,
      });
      const csvData = getFormat1CsvData({
        year: params.fiscalYear,
        reports: parsed,
        isDebugMode,
        projectDetailUrlType: params.projectDetailUrlType,
      });
      downloadCsv(csvData, fileName);
      break;
    }
    case 'rs-report2': {
      const parsed = ReportOrganizingTableFormat2Model.parse(jsonData);
      if (parsed.policies.length === 0) {
        throw new Error(EMPTY_DATA_ERROR);
      }

      const params = ReportOrganizingTableFilterSchema.parse({
        ...(batchParams as object),
        formatNumber: reportFormatType.Values.format2,
      });
      const csvData = getFormat2CsvData({
        year: params.fiscalYear,
        policies: parsed.policies,
        isDebugMode,
        projectDetailUrlType: params.projectDetailUrlType,
      });
      downloadCsv(csvData, fileName);
      break;
    }
    case 'rs-report3': {
      const parsed = ReportOrganizingTableFormat3Model.parse(jsonData);
      if (!parsed.projects.length) {
        throw new Error(EMPTY_DATA_ERROR);
      }
      const params = ReportOrganizingTableFilterSchema.parse({
        ...(batchParams as object),
        formatNumber: reportFormatType.Values.format3,
      });
      const csvData = getFormat3CsvData({
        year: params.fiscalYear,
        data: parsed.projects,
        isDebugMode,
        projectDetailUrlType: params.projectDetailUrlType,
      });
      downloadCsv(csvData, fileName);
      break;
    }
    case 'rs-report4': {
      const parsed = ReportOrganizingTableFormat4Model.parse(jsonData);
      if (!parsed.organizations.length) {
        throw new Error(EMPTY_DATA_ERROR);
      }

      const params = ReportOrganizingTableFilterSchema.parse({
        ...(batchParams as object),
        formatNumber: reportFormatType.Values.format4,
      });
      const csvData = getFormat4CsvData({
        year: params.fiscalYear,
        reports: parsed,
      });
      downloadCsv(csvData, fileName);
      break;
    }
    case 'rs-report5': {
      const parsed = ReportOrganizingTableFormat5Model.parse(jsonData);
      if (!parsed.policies.length) {
        throw new Error(EMPTY_DATA_ERROR);
      }

      const params = ReportOrganizingTableFilterSchema.parse({
        ...(batchParams as object),
        formatNumber: reportFormatType.Values.format5,
      });
      const csvData = getFormat5CsvData({
        year: params.fiscalYear,
        reports: parsed,
        isDebugMode,
        projectDetailUrlType: params.projectDetailUrlType,
      });
      downloadCsv(csvData, fileName);
      break;
    }
    case 'payment-group-upload': {
      const parsed = PaymentGroupUploadErrorModel.parse(jsonData);
      downloadCsv(Papa.unparse(parsed.csv), fileName);
      break;
    }
    case 'contractor-payment-upload': {
      const parsed = PaymentGroupUploadErrorModel.parse(jsonData);
      downloadCsv(Papa.unparse(parsed.csv), fileName);
      break;
    }
    case 'organizations-list': {
      const parsedData = OrganizationListRSModel.parse(jsonData);
      const csvData = getOrganizationListCsvRSData({ data: parsedData });
      downloadCsv(csvData, fileName);
      break;
    }
    case 'users_download_list': {
      const parsedData = UsersListModel.parse(jsonData);
      const csvData = getUserListCsvData({ data: parsedData });
      downloadCsv(csvData, fileName);
      break;
    }
    case 'users_upload_list': {
      downloadUsersUploadListCsv(jsonData, fileName);
      break;
    }
    default: {
      const check: never = csvTarget;
      throw new Error(`Unexpected target: ${check}`);
    }
  }
};
